import { SelectElementMapper } from 'src/app/_services/types/commons.type';
import { SharedFunnelDatalayerEvent } from 'src/app/_services/types/datalayer.types';
import { CoverageType } from 'src/app/enums/building.enums';
import { InfinityIconComponent } from 'src/app/ui-kit/icons/infinity-icon.component';
import { MoneyBillIconComponent } from 'src/app/ui-kit/icons/money-bill-icon.component';

import { BUILDING_INPUT_FORM_STEPS, COVERAGES } from '../../building/constants';
import { BUSINESS_INSURANCE_BASE } from '../company-insurance-constants';

export const BUSINESS_BUILDING_INSURANCE_BASE = 'assurance-batiment';

export const BUSINESS_BUILDING_FORM_URL = `${BUSINESS_INSURANCE_BASE}/${BUSINESS_BUILDING_INSURANCE_BASE}`;

export const BUILDING_FUNNEL_EVENT_BASE: SharedFunnelDatalayerEvent = {
  product_category: 'Market',
  product_name: 'Entreprise_Batiment',
  funnel_complete: false,
  get event() {
    return `${this.product_name}_${this.product_category}`;
  },
};

export const BUSINESS_BUILDING_INPUT_STEPS: Omit<
  typeof BUILDING_INPUT_FORM_STEPS,
  'CANCELLATION' | 'PERSONAL_INFO'
> = {
  INSURANCE_FORM: {
    ...BUILDING_INPUT_FORM_STEPS.INSURANCE_FORM,
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 1,
      stage_name: 'Informations',
    },
  },
  BUILDING_FORM: {
    ...BUILDING_INPUT_FORM_STEPS.BUILDING_FORM,
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 1,
      stage_name: 'Preneur assurance',
    },
  },
  COVERS_FORM: {
    ...BUILDING_INPUT_FORM_STEPS.COVERS_FORM,
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 2,
      stage_name: 'Couvertures',
    },
  },
  OFFER_SEARCH: {
    ...BUILDING_INPUT_FORM_STEPS.OFFER_SEARCH,
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 3,
      stage_name: 'Offres',
    },
  },
  LOGIN: {
    ...BUILDING_INPUT_FORM_STEPS.LOGIN,
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 4,
      stage_name: 'Connexion',
    },
  },
  REGISTER: {
    ...BUILDING_INPUT_FORM_STEPS.REGISTER,
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 4,
      stage_name: 'Inscription',
    },
  },
  QUESTIONNARY: {
    ...BUILDING_INPUT_FORM_STEPS.QUESTIONNARY,
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 5,
      stage_name: 'Questionnaire',
    },
  },
  END: {
    step: [-1],
    url: 'fin',
    datalayerEvent: {
      ...BUILDING_FUNNEL_EVENT_BASE,
      funnel_stage: 6,
      stage_name: 'Fin',
      funnel_complete: true,
    },
  },
};

export const BUSINESS_BUILDING_META_TITLE = $localize`Assurance Bâtiment en Suisse | Protégez votre Propriété | LTY`;

export const BUSINESS_BUILDING_META_DESCRIPTION = $localize`Protégez votre immeuble contre l'incendie, les dégâts d'eau et les risques naturels. Découvrez nos solutions d'assurance bâtiment, simples et claires.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUSINESS_BUILDING_TYPE_HELP_TEXT_TITLE = $localize`En quoi le type de bâtiment est-il important pour mon assurance ?`;

export const BUSINESS_BUILDING_TYPE_HELP_TEXT_CONTENT = $localize`Spécifier le type de bâtiment permet à l'assureur de comprendre les risques auxquels le bâtiment est exposé et de proposer une couverture adéquate pour protéger votre propriété.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUSINESS_BUILDING_INSURANCE_SUM_HELP_TITLE = $localize`Qu'est-ce qu'une somme d'assurance pour mon assurance bâtiment ?`;

export const BUSINESS_BUILDING_INSURANCE_SUM_HELP_CONTENT = $localize`La somme d'assurance correspond à la valeur estimée au moment de la souscription du bâtiment que vous souhaitez assurer. Il est important que ce montant reflète la réalité à l'instant actuel ; si la somme est sous-évaluée, en cas de sinistre vous ne percevrez pas l'entier de la somme des dommages. Si la somme est surévaluée, vous paierez des primes plus importantes.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUSINESS_BUILDING_SECURITY_MEASURE_HELP_TITLE = $localize`En quoi le fait d'avoir une alarme incendie change quelque chose pour mon assurance bâtiment ?`;

export const BUSINESS_BUILDING_SECURITY_MEASURE_HELP_CONTENT = $localize`Savoir si vous possédez une alarme incendie dans votre bâtiment est important car cette mesure peut réduire les risques et les pertes en cas de sinistre. Dans ce cas, par exemple, la présence d'une alarme incendie peut entraîner des réductions de prime ou des couvertures étendues pour certains types de sinistres.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUSINESS_BUILDING_HEATING_HELP_TITLE = $localize`En quoi le sytème de chauffage influence-t-il mon assurance bâtiment ?`;

export const BUSINESS_BUILDING_HEATING_HELP_CONTENT = $localize`Le type de système de système de chauffage dans un bâtiment peut avoir un impact sur le prix de l'assurance en raison de ses implications en termes de risques d'incendie, de risques environnementaux, de maintenance et de conformité aux normes de sécurité.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUSINESS_BUILDING_CONSTRUCTION_TYPE_HELP_TITLE = $localize`En quoi le genre de construction est-il important ?`;

export const BUSINESS_BUILDING_CONSTRUCTION_TYPE_HELP_CONTENT = $localize`Le genre de construction, qu'elle soit massive ou non massive, peut influencer le coût de l'assurance en raison de ses implications sur la résistance aux dommages et sur les coûts de réparation potentiels. Les assureurs prendront en compte ces facteurs lors de l'évaluation du risque et de la tarification de l'assurance pour un bâtiment spécifique.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUSINESS_BUILDING_ROOF_TYPE_HELP_TITLE = $localize`En quoi le type de toit est-il important ?`;

export const BUSINESS_BUILDING_ROOF_TYPE_HELP_CONTENT = $localize`Il s'agit ici d'évaluer les potentiels risques de chaque type de toit ; un toit plat a l'inconvénient potentiel d'être plus vulnérable aux fuites d'eau là ou un toit en pente a l'inconvénient d'être plus vulnérable aux fortes rafales de vent. Les assureurs prendront en compte ces facteurs lors de l'évaluation du risque et de la tarification pour s'assurer que le bâtiment est correctement couvert.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUSINESS_BUILDING_CIVIL_RESPONSABILITY_HELP_TITLE = $localize`Pourquoi devrais-je souscrire à une assurance responsabilité civile pour le bâtiment ?`;

export const BUSINESS_BUILDING_CIVIL_RESPONSABILITY_HELP_CONTENT = $localize`La responsabilité civile pour votre bâtiment vous protège si quelque chose se passe sur votre propriété et/ou que quelqu'un d'autre est blessé ou que ses biens sont endommagés (par exemple la tuile qui tombe de votre toit et qui atterit sur la voiture de votre voisin).
Cette option est importante, parce qu'elle te protège contre les risques financiers suite à un sinistre et vous permet de dormir sur vos deux oreilles. Plus le curseur ci-dessus sera haut, mieux vous pourrez dormir la nuit.`;

// eslint-disable-next-line no-irregular-whitespace
export const BUSINESS_BUILDING_DEDUCTIBLE_HELP_TITLE = $localize`En quoi la franchise est importante pour la détermination de la prime pour ma RC Bâtiment ?`;

export const BUSINESS_BUILDING_DEDUCTIBLE_HELP_CONTENT = $localize`La franchise est le montant que vous vous engagez à payer avant que l'assureur ne prenne en charge les frais de votre sinistre. Plus la franchise est haute, moins la prime le sera et vice versa. A vous de choisir le montant qui vous convient par rapport à votre budget.`;

export const BUSINESS_BUILDING_COVERAGES: SelectElementMapper<CoverageType> = {
  ...COVERAGES,
  [CoverageType.RENTAL_INCOME]: {
    value: CoverageType.RENTAL_INCOME,
    label: $localize`Revenu locatif`,
    iconComponent: MoneyBillIconComponent,
  },
  [CoverageType.ALL_RISK]: {
    value: CoverageType.ALL_RISK,
    label: $localize`All Risk`,
    iconComponent: InfinityIconComponent,
  },
};
